import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { Values } from '@lib/interfaces/form';
import usePrevious from '@lib/hooks/usePrevious';

interface UseFormPersistValuesProps {
  formId: string;
  persistForm: boolean;
  persistFormSeconds?: number;
}

type PersistedForm = {
  timestamp: number;
  values: Values;
};

const EMPTY_VALUES: Values = {};

function isExpired(form: PersistedForm, persistFormSeconds: number) {
  if (!persistFormSeconds) return false;
  const timeElapsed = (new Date().getTime() - form.timestamp) / 1000;
  return timeElapsed > persistFormSeconds;
}

const useFormPersistValues = ({
  formId,
  persistForm,
  persistFormSeconds = 0,
}: UseFormPersistValuesProps) => {
  const [form, setForm, removeForm] = useLocalStorage<
    PersistedForm | undefined
  >(formId, undefined);
  const prevPersistForm = usePrevious(persistForm);

  const setPersistedValues = useCallback(
    (values: Values) => {
      if (!persistForm) {
        return;
      }
      setForm({ values, timestamp: new Date().getTime() });
    },
    [persistForm, setForm],
  );

  // Read-only: a form that doesn't persist never sees another form's draft
  // stored under the same key, and expiry is cleaned up in an effect, not
  // during render.
  const getPersistedValues = (): Values => {
    if (
      !persistForm ||
      !form?.timestamp ||
      !form?.values ||
      isExpired(form, persistFormSeconds)
    ) {
      return EMPTY_VALUES;
    }
    return form.values;
  };

  useEffect(() => {
    const isDisabledNow = prevPersistForm === true && !persistForm;
    const hasExpired =
      persistForm && !!form?.timestamp && isExpired(form, persistFormSeconds);
    // Only clear a draft this form owned: a non-persisting form with the
    // same formId must not wipe it.
    if (form && (isDisabledNow || hasExpired)) {
      // removeForm deletes the key; setForm(undefined) would store "undefined"
      removeForm();
    }
  }, [form, persistForm, persistFormSeconds, prevPersistForm, removeForm]);

  return { setPersistedValues, getPersistedValues };
};

export default useFormPersistValues;
